/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import {
  Mail, FileText, Megaphone, ListTree, Users2, Send, AlertTriangle,
  Plus, Search, Trash2, Eye, UserCheck, BarChart3,
} from "lucide-react";
import MockedBanner from "@/components/MockedBanner";
import Modal from "@/components/Modal";
import ColumnChooser from "@/components/ColumnChooser";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";

const NAV = [
  { key: "campaigns", label: "Campaigns",        icon: Mail,        filter: (c) => true },
  { key: "drafts",    label: "Drafts",           icon: FileText,    filter: (c) => c.Status === "draft" },
  { key: "sent",      label: "Sent",             icon: Send,        filter: (c) => c.Status === "sent" },
  { key: "failed",    label: "Failed & Bounced", icon: AlertTriangle, filter: (c) => (c.failed_count || 0) > 0 },
  { key: "lists",     label: "Lists",            icon: ListTree,    section: true },
  { key: "groups",    label: "Recipient Groups", icon: Users2,      section: true },
  { key: "reseller",  label: "Email Reseller",   icon: UserCheck,   section: true },
  { key: "templates", label: "Templates",        icon: BarChart3,   section: true },
];

export default function MarketingPage() {
  const [tab, setTab] = useState("campaigns");
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Marketing</h1>
          <p className="text-sm text-zinc-500 mt-1">Campaigns, recipient lists, groups and templates.</p>
        </div>
      </div>
      <MockedBannerIfMocked />

      <div className="grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-4 lg:min-h-[calc(100vh-19rem)]">
        {/* Sidebar */}
        <aside className="es-card overflow-hidden">
          <nav className="py-2">
            {NAV.map((n, i) => {
              const Icon = n.icon;
              const active = tab === n.key;
              return (
                <div key={n.key}>
                  {n.section && i > 0 && NAV[i - 1] && !NAV[i - 1].section && (
                    <div className="border-t border-zinc-100 my-2" />
                  )}
                  <button
                    data-testid={`marketing-tab-${n.key}`}
                    onClick={() => setTab(n.key)}
                    className={[
                      "w-full text-left flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors",
                      active
                        ? "bg-brand-50 text-brand-800 font-semibold border-l-2 border-brand-600"
                        : "text-zinc-700 hover:bg-zinc-50 border-l-2 border-transparent",
                    ].join(" ")}
                  >
                    <Icon className={`w-4 h-4 ${active ? "text-brand-600" : "text-zinc-500"}`} />
                    {n.label}
                  </button>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <section className="space-y-4">
          {(tab === "campaigns" || tab === "drafts" || tab === "sent" || tab === "failed") && (
            <CampaignsView filterKey={tab} />
          )}
          {tab === "lists" && <ListsView />}
          {tab === "groups" && <GroupsView />}
          {tab === "reseller" && <ResellerView />}
          {tab === "templates" && <TemplatesView />}
        </section>
      </div>
    </div>
  );
}

// ─── Campaigns ────────────────────────────────────────────────────────────────
function CampaignsView({ filterKey }) {
  const toast = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [sending, setSending] = useState(null);
  const [safeSendFor, setSafeSendFor] = useState(null);  // campaign currently in the safe-send modal

  const load = async () => {
    try {
      const [c, t, l, g] = await Promise.all([
        api.get("/marketing-campaigns"), api.get("/email-templates"),
        api.get("/marketing-lists"), api.get("/marketing-groups"),
      ]);
      setCampaigns(c.data); setTemplates(t.data); setLists(l.data); setGroups(g.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const filter = NAV.find((n) => n.key === filterKey)?.filter || (() => true);
  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    return campaigns.filter((c) => filter(c) && (!term || c.Name.toLowerCase().includes(term)));
  }, [campaigns, q, filterKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const sent = campaigns.filter((c) => c.Status === "sent");
    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    return {
      sent_month: sent.filter((c) => c.Last_Sent_At && new Date(c.Last_Sent_At) >= monthStart).length,
      total_recipients: campaigns.reduce((s, c) => s + (c.sent_count || 0) + (c.failed_count || 0), 0),
      delivered: campaigns.reduce((s, c) => s + (c.sent_count || 0), 0),
      failed: campaigns.reduce((s, c) => s + (c.failed_count || 0), 0),
    };
  }, [campaigns]);

  const sendCampaign = async (c) => {
    // Real send is gated behind the SafeSendModal — opens preview, type-to-confirm
    // and a "send to me only" test option before the actual blast.
    setSafeSendFor(c);
  };

  const doRealSend = async (c) => {
    setSending(c.id);
    try {
      const { data } = await api.post(`/marketing-campaigns/${c.id}/send`);
      toast.success(data.message);
      setSafeSendFor(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(null); }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.Name}"?`)) return;
    try { await api.delete(`/marketing-campaigns/${c.id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const tabLabel = NAV.find((n) => n.key === filterKey)?.label || "Campaigns";

  const allColumns = [
    {
      key: "Name", label: "Name",
      cell: (c) => {
        const tpl = templates.find((t) => t.id === c.Template_ID);
        return (
          <>
            <div className="font-semibold text-zinc-900">{c.Name}</div>
            <div className="text-xs text-zinc-500">{tpl?.Subject || "—"}</div>
          </>
        );
      },
    },
    { key: "Recipients", label: "Recipients", cell: (c) => <span className="text-xs text-zinc-600">{describeRecipients(c, lists, groups)}</span> },
    { key: "Sent", label: "Sent", align: "right", cell: (c) => <span className="font-mono">{c.sent_count || 0}</span> },
    { key: "Failed", label: "Failed", align: "right", cell: (c) => <span className={`font-mono ${(c.failed_count || 0) > 0 ? "text-red-600" : ""}`}>{c.failed_count || 0}</span> },
    { key: "Status", label: "Status", cell: (c) => <span className={statusClass(c.Status)}>{c.Status}</span> },
    { key: "Last_Sent_At", label: "Last sent", cell: (c) => <span className="text-xs font-mono text-zinc-500">{c.Last_Sent_At?.slice(0, 10) || "—"}</span> },
    { key: "Created", label: "Created", defaultVisible: false, cell: (c) => <span className="text-xs font-mono text-zinc-500">{c.created_date?.slice(0, 10) || "—"}</span> },
    {
      key: "_actions", label: "Actions", locked: true, align: "right",
      cell: (c) => (
        <div className="flex items-center justify-end gap-2">
          {c.Status === "draft" && (
            <button
              data-testid={`campaign-send-${c.id}`}
              disabled={sending === c.id}
              onClick={() => sendCampaign(c)}
              className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-brand-600 text-white hover:bg-brand-700"
            >
              <Send className="w-3 h-3" /> {sending === c.id ? "Sending…" : "Send"}
            </button>
          )}
          <button onClick={() => onDelete(c)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      ),
    },
  ];

  const { visibleColumns, orderedColumns, isVisible, toggleColumn, moveColumn, reset } =
    useColumnPrefs("marketing_campaigns", allColumns);

  return (
    <>
      <div className="es-card p-4 flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-xl font-semibold">{tabLabel}</h2>
        <div className="flex items-center gap-2">
          <ColumnChooser
            tableLabel="marketing_campaigns"
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            moveColumn={moveColumn}
            reset={reset}
          />
          <button data-testid="marketing-new-campaign" onClick={() => setShowNew(true)} className="es-btn-primary">
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Mail}   color="brand"  value={stats.sent_month}      label="Campaigns Sent"   sublabel="this month" />
        <StatCard icon={Users2} color="blue"   value={stats.total_recipients} label="Total Recipients" sublabel="across all campaigns" />
        <StatCard icon={Send}   color="emerald" value={stats.delivered}       label="Emails Sent"      sublabel="total delivered" />
        <StatCard icon={UserCheck} color="amber" value={stats.failed}         label="Failed / Bounced" sublabel="delivery failures" />
      </div>

      <div className="es-card p-4 flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="marketing-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search campaigns…" className="es-input pl-9" />
        </div>
        <div className="text-xs text-zinc-500">{filtered.length} campaign{filtered.length === 1 ? "" : "s"}</div>
      </div>

      <div className="es-card">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-sm text-zinc-400">
            <FileText className="w-10 h-10 mx-auto mb-3 text-zinc-200" />
            No campaigns here yet
          </div>
        ) : (
          <table className="es-table">
            <thead>
              <tr>
                {visibleColumns.map((c) => (
                  <th key={c.key} className={[c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "", c.width || ""].join(" ")}>
                    {c.header ? c.header() : c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`campaign-row-${c.id}`}>
                  {visibleColumns.map((col) => (
                    <td key={col.key} className={col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : ""}>{col.cell(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && (
        <NewCampaignModal
          templates={templates} lists={lists} groups={groups}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
      {safeSendFor && (
        <SafeSendModal
          campaign={safeSendFor}
          templates={templates}
          lists={lists}
          groups={groups}
          sending={sending === safeSendFor.id}
          onClose={() => setSafeSendFor(null)}
          onConfirmSend={() => doRealSend(safeSendFor)}
        />
      )}
    </>
  );
}

function describeRecipients(c, lists, groups) {
  const t = (c.Recipients_Type || "filter").toLowerCase();
  if (t === "list") return `List · ${lists.find((l) => l.id === c.List_ID)?.Name || "—"}`;
  if (t === "group") return `Group · ${groups.find((g) => g.id === c.Group_ID)?.Name || "—"}`;
  if (t === "company") return `Single company`;
  if (c.Target_Filter && Object.keys(c.Target_Filter).length) {
    return Object.entries(c.Target_Filter).map(([k, v]) => `${k}=${v}`).join(" · ");
  }
  return "All active customers";
}

function statusClass(s) {
  if (s === "sent") return "es-badge es-badge-success";
  if (s === "draft") return "es-badge es-badge-neutral";
  if (s === "scheduled") return "es-badge es-badge-info";
  return "es-badge es-badge-neutral";
}

// ─── New campaign modal ──────────────────────────────────────────────────────
function NewCampaignModal({ templates, lists, groups, onClose, onCreated }) {
  const [form, setForm] = useState({
    Name: "", Template_ID: "", Subject_Override: "",
    Recipients_Type: "filter", Target_Filter: {}, List_ID: "", Group_ID: "", Company_ID: "",
  });
  const [tier, setTier] = useState("");
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {}); }, []);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const payload = { ...form };
      if (payload.Recipients_Type === "filter") {
        payload.Target_Filter = tier ? { Account_Tier: tier } : {};
      }
      await api.post("/marketing-campaigns", payload);
      onCreated();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New Campaign" onClose={onClose} size="xl">
      <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
        <div>
          <label className="es-label">Name *</label>
          <input data-testid="campaign-name" required value={form.Name} onChange={(e) => u("Name", e.target.value)} className="es-input" />
        </div>
        <div>
          <label className="es-label">Template *</label>
          <select data-testid="campaign-template" required value={form.Template_ID} onChange={(e) => u("Template_ID", e.target.value)} className="es-select w-full">
            <option value="">Select…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.Template_Name} — {t.Subject}</option>)}
          </select>
        </div>
        <div>
          <label className="es-label">Override subject (optional)</label>
          <input value={form.Subject_Override} onChange={(e) => u("Subject_Override", e.target.value)} placeholder="Leave blank to use the template subject" className="es-input" />
        </div>

        <div>
          <label className="es-label">Recipients</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { v: "filter",  l: "Filter by tier" },
              { v: "group",   l: "Saved group" },
              { v: "list",    l: "Static list" },
              { v: "company", l: "Single company" },
            ].map((opt) => (
              <label key={opt.v} data-testid={`campaign-recipients-${opt.v}`} className={`border rounded-lg px-3 py-2 cursor-pointer ${form.Recipients_Type === opt.v ? "border-brand-500 bg-brand-50/40" : "border-zinc-200"}`}>
                <input type="radio" name="rcpt" checked={form.Recipients_Type === opt.v} onChange={() => u("Recipients_Type", opt.v)} className="sr-only" />
                <div className="text-sm font-semibold">{opt.l}</div>
              </label>
            ))}
          </div>
        </div>

        {form.Recipients_Type === "filter" && (
          <div>
            <label className="es-label">Account tier (leave blank to send to all)</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)} className="es-select w-full">
              <option value="">All tiers</option>
              {["Enterprise", "Premium", "Standard"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}
        {form.Recipients_Type === "group" && (
          <div>
            <label className="es-label">Group *</label>
            <select required value={form.Group_ID} onChange={(e) => u("Group_ID", e.target.value)} className="es-select w-full">
              <option value="">Select…</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.Name}</option>)}
            </select>
          </div>
        )}
        {form.Recipients_Type === "list" && (
          <div>
            <label className="es-label">List *</label>
            <select required value={form.List_ID} onChange={(e) => u("List_ID", e.target.value)} className="es-select w-full">
              <option value="">Select…</option>
              {lists.map((l) => <option key={l.id} value={l.id}>{l.Name} ({(l.Recipients || []).length})</option>)}
            </select>
          </div>
        )}
        {form.Recipients_Type === "company" && (
          <div>
            <label className="es-label">Company *</label>
            <select required value={form.Company_ID} onChange={(e) => u("Company_ID", e.target.value)} className="es-select w-full">
              <option value="">Select…</option>
              {companies.map((co) => <option key={co.id} value={co.id}>{co.Company_Name}</option>)}
            </select>
          </div>
        )}

        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} data-testid="campaign-save" className="es-btn-primary">{saving ? "Saving…" : "Create campaign"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Lists ───────────────────────────────────────────────────────────────────
function ListsView() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = async () => { try { const { data } = await api.get("/marketing-lists"); setItems(data); } catch (e) { toast.error(e.message); } };
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this list?")) return;
    try { await api.delete(`/marketing-lists/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <>
      <div className="es-card p-4 flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Lists</h2>
        <button data-testid="marketing-new-list" onClick={() => setShowNew(true)} className="es-btn-primary"><Plus className="w-4 h-4" /> New list</button>
      </div>
      <div className="es-card">
        {items.length === 0 ? (
          <div className="text-center py-16 text-sm text-zinc-400">No lists yet. Click "New list" to upload one.</div>
        ) : (
          <table className="es-table">
            <thead><tr><th>Name</th><th>Recipients</th><th>Description</th><th></th></tr></thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} data-testid={`list-row-${l.id}`}>
                  <td className="font-semibold">{l.Name}</td>
                  <td className="font-mono">{(l.Recipients || []).length}</td>
                  <td className="text-sm text-zinc-600">{l.Description}</td>
                  <td className="text-right"><button onClick={() => onDelete(l.id)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showNew && <NewListModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </>
  );
}

function NewListModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const parseRecipients = () =>
    text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).map((line) => {
      // Accept "Name <email@x.com>" or just "email@x.com" or "email,name"
      const m = line.match(/^([^<]+)<([^>]+)>$/);
      if (m) return { Name: m[1].trim(), Email: m[2].trim() };
      const parts = line.split(",").map((s) => s.trim());
      if (parts.length === 2 && parts[1].includes("@")) return { Name: parts[0], Email: parts[1] };
      if (parts.length === 2) return { Name: parts[1], Email: parts[0] };
      return { Name: "", Email: line };
    });

  const onSubmit = async (e) => {
    e.preventDefault();
    const Recipients = parseRecipients().filter((r) => r.Email.includes("@"));
    if (!Recipients.length) { setErr("No valid emails found."); return; }
    setSaving(true);
    try {
      await api.post("/marketing-lists", { Name: name, Description: desc, Recipients });
      onCreated();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const preview = parseRecipients().slice(0, 5);

  return (
    <Modal title="New Recipient List" onClose={onClose}>
      <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
        <div><label className="es-label">Name *</label><input required value={name} onChange={(e) => setName(e.target.value)} className="es-input" data-testid="list-name" /></div>
        <div><label className="es-label">Description</label><input value={desc} onChange={(e) => setDesc(e.target.value)} className="es-input" /></div>
        <div>
          <label className="es-label">Recipients (one per line)</label>
          <p className="text-xs text-zinc-500 mb-2">Formats: <code className="bg-zinc-100 px-1">email@x.com</code>, <code className="bg-zinc-100 px-1">Name &lt;email@x.com&gt;</code> or <code className="bg-zinc-100 px-1">Name,email@x.com</code></p>
          <textarea data-testid="list-recipients" rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder="Liam O'Brien <liam@example.com>" className="es-input h-auto py-2 font-mono text-sm" />
          {preview.length > 0 && (
            <div className="text-xs mt-2 text-zinc-500">Preview: {preview.map((p, i) => <span key={i}>{p.Name || "—"} · {p.Email}{i < preview.length - 1 ? "; " : ""}</span>)}{parseRecipients().length > 5 ? `… +${parseRecipients().length - 5} more` : ""}</div>
          )}
        </div>
        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} data-testid="list-save" className="es-btn-primary">{saving ? "Saving…" : "Create list"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Groups ──────────────────────────────────────────────────────────────────
function GroupsView() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [previewCache, setPreviewCache] = useState({});

  const load = async () => { try { const { data } = await api.get("/marketing-groups"); setItems(data); } catch (e) { toast.error(e.message); } };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    items.forEach((g) => {
      if (previewCache[g.id] !== undefined) return;
      api.get(`/marketing-groups/${g.id}/preview`).then(({ data }) =>
        setPreviewCache((p) => ({ ...p, [g.id]: data.match_count }))
      ).catch(() => {});
    });
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDelete = async (id) => {
    if (!window.confirm("Delete this group?")) return;
    try { await api.delete(`/marketing-groups/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <>
      <div className="es-card p-4 flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Recipient Groups</h2>
        <button data-testid="marketing-new-group" onClick={() => setShowNew(true)} className="es-btn-primary"><Plus className="w-4 h-4" /> New group</button>
      </div>
      <div className="es-card">
        {items.length === 0 ? (
          <div className="text-center py-16 text-sm text-zinc-400">No groups yet — try "Enterprise Resellers" or "Standard Resellers".</div>
        ) : (
          <table className="es-table">
            <thead><tr><th>Name</th><th>Filter</th><th>Companies matched</th><th></th></tr></thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.id} data-testid={`group-row-${g.id}`}>
                  <td className="font-semibold">{g.Name}<div className="text-xs text-zinc-500">{g.Description}</div></td>
                  <td className="text-xs font-mono">{Object.entries(g.Filter || {}).map(([k, v]) => `${k}=${v}`).join(" · ") || "—"}</td>
                  <td className="font-mono">{previewCache[g.id] ?? "—"}</td>
                  <td className="text-right"><button onClick={() => onDelete(g.id)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showNew && <NewGroupModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </>
  );
}

function NewGroupModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [tier, setTier] = useState("Enterprise");
  const [portal, setPortal] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    const Filter = {};
    if (tier) Filter.Account_Tier = tier;
    if (portal) Filter.Portal_Access_Enabled = true;
    try { await api.post("/marketing-groups", { Name: name, Description: desc, Filter }); onCreated(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New Recipient Group" onClose={onClose} size="md">
      <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
        <div><label className="es-label">Name *</label><input required value={name} onChange={(e) => setName(e.target.value)} className="es-input" data-testid="group-name" placeholder="Enterprise Resellers" /></div>
        <div><label className="es-label">Description</label><input value={desc} onChange={(e) => setDesc(e.target.value)} className="es-input" /></div>
        <div><label className="es-label">Account Tier</label>
          <select value={tier} onChange={(e) => setTier(e.target.value)} className="es-select w-full">
            <option value="">Any</option>
            {["Enterprise", "Premium", "Standard"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={portal} onChange={(e) => setPortal(e.target.checked)} /> Portal access enabled only</label>
        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}
        <div className="flex justify-end gap-3"><button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button><button type="submit" disabled={saving} data-testid="group-save" className="es-btn-primary">{saving ? "Saving…" : "Create group"}</button></div>
      </form>
    </Modal>
  );
}

// ─── Email Reseller (single-company quick send) ──────────────────────────────
function ResellerView() {
  const toast = useToast();
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [tplId, setTplId] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/email-templates")]).then(([c, t]) => {
      setCompanies(c.data.filter((co) => co.Portal_Access_Enabled));
      setTemplates(t.data);
    });
  }, []);

  const send = async () => {
    if (!companyId || !tplId) { toast.error("Pick a company and a template."); return; }
    setSending(true);
    try {
      const co = companies.find((c) => c.id === companyId);
      const { data: campaign } = await api.post("/marketing-campaigns", {
        Name: `Direct to ${co?.Company_Name} — ${new Date().toLocaleDateString()}`,
        Template_ID: tplId,
        Subject_Override: subject,
        Recipients_Type: "company",
        Company_ID: companyId,
      });
      const { data } = await api.post(`/marketing-campaigns/${campaign.id}/send`);
      toast.success(data.message);
      setSubject(""); setCompanyId(""); setTplId("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(false); }
  };

  return (
    <>
      <div className="es-card p-4">
        <h2 className="font-display text-xl font-semibold">Email a single reseller</h2>
        <p className="text-sm text-zinc-500 mt-1">Pick a portal-enabled reseller + template — we'll fire a one-off campaign to their contacts.</p>
      </div>
      <div className="es-card p-6 space-y-4">
        <div><label className="es-label">Reseller *</label>
          <select data-testid="reseller-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="es-select w-full">
            <option value="">Select reseller…</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.Company_Name} ({c.Account_Tier})</option>)}
          </select>
        </div>
        <div><label className="es-label">Template *</label>
          <select data-testid="reseller-template" value={tplId} onChange={(e) => setTplId(e.target.value)} className="es-select w-full">
            <option value="">Select template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.Template_Name} — {t.Subject}</option>)}
          </select>
        </div>
        <div><label className="es-label">Override subject</label><input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Leave blank for template default" className="es-input" /></div>
        <button data-testid="reseller-send" disabled={sending} onClick={send} className="es-btn-primary"><Send className="w-4 h-4" /> {sending ? "Sending…" : "Send now"}</button>
      </div>
    </>
  );
}

// ─── Templates (reuse email_templates) ───────────────────────────────────────
function TemplatesView() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => { try { const { data } = await api.get("/email-templates"); setItems(data); } catch (e) { toast.error(e.message); } };
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/email-templates/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <>
      <div className="es-card p-4 flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Templates</h2>
        <button data-testid="marketing-new-template" onClick={() => setShowNew(true)} className="es-btn-primary"><Plus className="w-4 h-4" /> New template</button>
      </div>
      <div className="es-card">
        {items.length === 0 ? (
          <div className="text-center py-16 text-sm text-zinc-400">No templates yet.</div>
        ) : (
          <table className="es-table">
            <thead><tr><th>Name</th><th>Type</th><th>Subject</th><th></th></tr></thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} data-testid={`template-row-${t.id}`}>
                  <td className="font-semibold">{t.Template_Name}</td>
                  <td><span className="es-badge es-badge-neutral">{t.Template_Type}</span></td>
                  <td className="text-sm text-zinc-600">{t.Subject}</td>
                  <td className="text-right">
                    <button onClick={() => setPreview(t)} className="text-zinc-400 hover:text-brand-700 p-1 mr-1"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(t.id)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showNew && <NewTemplateModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {preview && (
        <Modal title={`Preview · ${preview.Template_Name}`} onClose={() => setPreview(null)} size="xl">
          <div className="px-6 py-5 space-y-3">
            <div className="text-sm"><strong>Subject:</strong> {preview.Subject}</div>
            <div className="border border-zinc-200 rounded-lg p-4 bg-zinc-50 text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: preview.Body_HTML || "<em>(empty)</em>" }} />
          </div>
        </Modal>
      )}
    </>
  );
}

function NewTemplateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ Template_Name: "", Subject: "", Body_HTML: "", Template_Type: "marketing" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [sampleRecipient, setSampleRecipient] = useState(null);
  const bodyRef = useRef(null);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load one real contact+company so the live preview shows realistic substitutions.
  useEffect(() => {
    (async () => {
      try {
        const [c, co] = await Promise.all([api.get("/contacts"), api.get("/companies")]);
        const contact = c.data[0];
        const company = contact ? co.data.find((x) => x.id === contact.Company_ID) : co.data[0];
        setSampleRecipient({ contact, company });
      } catch { /* ignore */ }
    })();
  }, []);

  const VARS = [
    { token: "{{contact.First_Name}}", label: "First Name" },
    { token: "{{contact.Last_Name}}",  label: "Last Name" },
    { token: "{{contact.Email}}",      label: "Email" },
    { token: "{{company.Company_Name}}", label: "Company" },
    { token: "{{company.Account_Tier}}", label: "Tier" },
    { token: "{{company.POCity}}",       label: "City" },
    { token: "{{date.today}}",           label: "Today" },
  ];

  const insertVar = (token, field) => {
    const el = field === "subject" ? document.querySelector('[data-testid="template-subject"]') : bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? form[field === "subject" ? "Subject" : "Body_HTML"].length;
    const end = el.selectionEnd ?? start;
    const key = field === "subject" ? "Subject" : "Body_HTML";
    const cur = form[key];
    const next = cur.slice(0, start) + token + cur.slice(end);
    u(key, next);
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; }, 0);
  };

  const render = (text) => {
    if (!text) return "";
    const c = sampleRecipient?.contact || {};
    const co = sampleRecipient?.company || {};
    const scopes = {
      contact: { First_Name: c.First_Name, Last_Name: c.Last_Name, Email: c.Email },
      company: { Company_Name: co.Company_Name, Account_Tier: co.Account_Tier, POCity: co.POCity },
      date: { today: new Date().toISOString().slice(0, 10) },
    };
    return text.replace(/\{\{\s*([\w\.]+)\s*\}\}/g, (_, path) => {
      const parts = path.split(".");
      let cur = scopes;
      for (const p of parts) cur = cur?.[p];
      return cur ?? "";
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try { await api.post("/email-templates", form); onCreated(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New Template" onClose={onClose} size="2xl">
      <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="es-label">Template Name *</label><input required value={form.Template_Name} onChange={(e) => u("Template_Name", e.target.value)} className="es-input" data-testid="template-name" /></div>
          <div><label className="es-label">Type</label>
            <select value={form.Template_Type} onChange={(e) => u("Template_Type", e.target.value)} className="es-select w-full">
              {["marketing", "low_stock", "order", "generic"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="es-label">Subject *</label>
            <div className="flex flex-wrap gap-1 mb-1">
              {VARS.slice(0, 4).map((v) => (
                <button key={v.token} type="button" onClick={() => insertVar(v.token, "subject")}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 hover:bg-brand-100 text-zinc-600">{v.label}</button>
              ))}
            </div>
          </div>
          <input required value={form.Subject} onChange={(e) => u("Subject", e.target.value)} className="es-input" data-testid="template-subject" />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="es-label">Body HTML *</label>
            <div className="text-[10px] text-zinc-500">Click a variable to insert at cursor</div>
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            {VARS.map((v) => (
              <button key={v.token} type="button"
                data-testid={`template-var-${v.token.replace(/[^a-z0-9]/gi, "")}`}
                onClick={() => insertVar(v.token, "body")}
                className="text-[11px] px-2 py-0.5 rounded-full bg-brand-50 hover:bg-brand-100 text-brand-800 font-mono border border-brand-200">
                {v.token}
              </button>
            ))}
          </div>
          <textarea required ref={bodyRef} rows={10} value={form.Body_HTML} onChange={(e) => u("Body_HTML", e.target.value)} className="es-input h-auto py-3 font-mono text-xs" data-testid="template-body" placeholder="Hi {{contact.First_Name}}, …" />
        </div>

        {/* Live preview */}
        <div>
          <label className="es-label">Live Preview {sampleRecipient?.contact ? <span className="text-zinc-400 font-normal text-[10px]">(using {sampleRecipient.contact.First_Name} {sampleRecipient.contact.Last_Name} · {sampleRecipient.company?.Company_Name})</span> : <span className="text-zinc-400 font-normal text-[10px]">(no contacts seeded)</span>}</label>
          <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50">
            <div className="text-xs text-zinc-500 mb-1">Subject</div>
            <div className="text-sm font-semibold mb-3" data-testid="template-preview-subject">{render(form.Subject) || <span className="text-zinc-300">—</span>}</div>
            <div className="text-xs text-zinc-500 mb-1">Body</div>
            <div className="text-sm whitespace-pre-wrap" data-testid="template-preview-body" dangerouslySetInnerHTML={{ __html: render(form.Body_HTML) || "<span class='text-zinc-300'>—</span>" }} />
          </div>
        </div>

        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}
        <div className="flex justify-end gap-3"><button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button><button type="submit" disabled={saving} data-testid="template-save" className="es-btn-primary">{saving ? "Saving…" : "Create template"}</button></div>
      </form>
    </Modal>
  );
}

// ─── Mocked-banner gate ──────────────────────────────────────────────────────
// Only surface the "mocked" warning when SMTP isn't actually wired. With
// real SMTP active (the common case), users don't need to see it.
function MockedBannerIfMocked() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    api.get("/admin/smtp/status")
      .then(({ data }) => setShow(!(data?.configured && data?.has_password)))
      .catch(() => setShow(false));
  }, []);
  if (!show) return null;
  return (
    <MockedBanner>
      SMTP isn&apos;t configured yet — campaigns will compute recipients and queue them as
      <em> pending</em>, but no email leaves the server until you wire credentials in
      <em> Settings → Email</em>.
    </MockedBanner>
  );
}

// ─── Safe Send modal ─────────────────────────────────────────────────────────
/**
 * Pre-flight check before a real campaign blast.
 *  1. Pulls /preview from the server (rendered subject + body for the first
 *     recipient, total recipient count, signature appended).
 *  2. "Send to me only" — fires /send-test so the user can sanity-check
 *     formatting in their own inbox without touching the recipient list.
 *  3. Type-to-confirm — user must type the literal recipient count or
 *     "SEND" before the real Send button enables.
 */
function SafeSendModal({ campaign, templates, lists, groups, sending, onClose, onConfirmSend }) {
  const toast = useToast();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setLoading(true); setErr("");
    api.get(`/marketing-campaigns/${campaign.id}/preview`)
      .then(({ data }) => setPreview(data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [campaign.id]);

  const total = preview?.total_recipients ?? 0;
  const requiredText = total > 0 ? String(total) : "SEND";
  const canSend = !loading && !sending && !err && total > 0 && preview?.smtp_ready && confirmText.trim() === requiredText;

  const sendToMe = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/marketing-campaigns/${campaign.id}/send-test`);
      toast.success(`Test email sent to ${data.delivered_to}. Check your inbox.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setTesting(false); }
  };

  const tpl = templates.find((t) => t.id === campaign.Template_ID);
  const audience = describeRecipients(campaign, lists, groups);

  return (
    <Modal title={`Safe Send · ${campaign.Name}`} onClose={onClose} size="2xl">
      <div className="px-6 py-5 space-y-5">
        {/* Audience summary */}
        <div className="grid grid-cols-3 gap-3" data-testid="safe-send-audience">
          <div className="es-card p-3 border-2 border-brand-200 bg-brand-50/30">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-mono">Recipients</div>
            <div className="text-2xl font-bold text-brand-700">{loading ? "…" : total}</div>
            <div className="text-[10px] text-zinc-500">{audience}</div>
          </div>
          <div className="es-card p-3">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-mono">Template</div>
            <div className="text-sm font-semibold text-zinc-900 truncate">{tpl?.Template_Name || "—"}</div>
            <div className="text-[10px] text-zinc-500">{tpl?.Template_Type || "—"}</div>
          </div>
          <div className="es-card p-3">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-mono">SMTP</div>
            <div className={`text-sm font-semibold ${preview?.smtp_ready ? "text-emerald-600" : "text-amber-600"}`}>
              {loading ? "…" : preview?.smtp_ready ? "Ready" : "Not configured"}
            </div>
            <div className="text-[10px] text-zinc-500">
              {preview?.smtp_ready ? "Real emails will be sent" : "Recipients will be queued only"}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-2" data-testid="safe-send-preview">
          <div className="flex items-center justify-between">
            <div className="es-overline">Preview · first recipient</div>
            {preview?.sample_recipient?.Email && (
              <div className="text-[10px] font-mono text-zinc-500 truncate max-w-xs" title={preview.sample_recipient.Email}>
                {preview.sample_recipient.Name || preview.sample_recipient.Email} ·{" "}
                {preview.sample_recipient.Company_Name || "—"}
              </div>
            )}
          </div>
          {loading ? (
            <div className="border border-zinc-200 rounded-lg p-6 text-center text-xs text-zinc-400 bg-zinc-50">
              Rendering preview…
            </div>
          ) : err ? (
            <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>
          ) : (
            <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
              <div className="px-4 py-2 border-b border-zinc-100 bg-zinc-50 text-xs">
                <span className="text-zinc-500">Subject:&nbsp;</span>
                <span className="font-semibold text-zinc-900">{preview?.subject || "(no subject)"}</span>
              </div>
              <div
                className="p-4 max-h-72 overflow-y-auto text-sm"
                dangerouslySetInnerHTML={{ __html: preview?.rendered_html || "<em>(empty body)</em>" }}
                data-testid="safe-send-preview-html"
              />
            </div>
          )}
        </div>

        {/* Merge tag reference (read-only chips so users know what's available) */}
        <div className="border border-dashed border-zinc-200 rounded-lg p-3 bg-zinc-50/50">
          <div className="es-overline mb-2 text-zinc-500">Merge tags available in templates</div>
          <div className="flex flex-wrap gap-1">
            {[
              "{{contact.First_Name}}",
              "{{contact.Last_Name}}",
              "{{contact.Email}}",
              "{{company.Company_Name}}",
              "{{company.Account_Tier}}",
              "{{date.today}}",
            ].map((t) => (
              <span
                key={t}
                className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-white border border-zinc-200 text-zinc-700"
              >
                {t}
              </span>
            ))}
          </div>
          <p className="text-[10px] text-zinc-500 mt-2">
            Edit a template to insert these. Personal signature is appended automatically from <em>My Profile</em>.
          </p>
        </div>

        {/* Test send */}
        <div className="border border-zinc-200 rounded-lg p-3 flex items-center justify-between gap-3 bg-white">
          <div>
            <div className="text-sm font-semibold text-zinc-900">Send a test to me only</div>
            <div className="text-xs text-zinc-500">Fires one email to your own address so you can sanity-check before going wide.</div>
          </div>
          <button
            data-testid="safe-send-test-btn"
            onClick={sendToMe}
            disabled={testing || loading || !preview?.smtp_ready}
            className="es-btn-secondary whitespace-nowrap"
          >
            <Send className="w-3.5 h-3.5" /> {testing ? "Sending…" : "Send to me"}
          </button>
        </div>

        {/* Type-to-confirm */}
        <div className="border-2 border-amber-200 bg-amber-50/50 rounded-lg p-4 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-zinc-900">Confirm the mass send</div>
              <div className="text-xs text-zinc-700">
                Type <code className="bg-white px-1.5 py-0.5 rounded border border-amber-300 font-mono text-amber-800">{requiredText}</code> below to enable the Send button.
              </div>
            </div>
          </div>
          <input
            data-testid="safe-send-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`Type "${requiredText}" to enable Send`}
            disabled={loading || total === 0}
            className="es-input"
            autoComplete="off"
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="es-btn-secondary" disabled={sending}>Cancel</button>
          <button
            data-testid="safe-send-confirm-btn"
            onClick={onConfirmSend}
            disabled={!canSend}
            className="es-btn-primary"
          >
            <Send className="w-4 h-4" /> {sending ? "Sending…" : `Send to ${total} recipient${total === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}


// ─── Stat card ───────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, value, label, sublabel, color }) {
  const colors = {
    brand: "bg-brand-50 text-brand-700",
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="es-card p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-zinc-900 leading-tight">{value}</div>
        <div className="text-xs text-zinc-700 font-semibold leading-tight">{label}</div>
        <div className="text-[10px] text-zinc-500 leading-tight mt-0.5">{sublabel}</div>
      </div>
    </div>
  );
}
